import { createSelector } from '@ngrx/store';
import {
  getJobsiteState,
  getSelectedBusinessLine,
  getMasterContractState,
  getSelectedShippingConditions,
  getSpecificationPage,
  getPointOfDeliveryState,
} from '../../../reducers';
import { ISalesDocument } from '@cemex-core/cmx-api/models/product/models/sales-document.interface';
import { Moment } from 'moment';
import { ContractBalance } from '../models/contract-balance';
import { _safe } from '../../../../../modules/shared/utils/safe-get.method';

/**
 * Returns contract request data (jobsite + product-type + shipment) as an anonymous object
 * @type {MemoizedSelector<object, {jobsite: IJobsiteState; businessLine: BusinessLine; shippingCondition: ShippingCondition}>}
 */
export const getContractRequestData = createSelector(
  getJobsiteState,
  getPointOfDeliveryState,
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  (jobsite, pointOfDelivery, businessLine, shippingCondition) => {
    return {
      jobsite,
      pointOfDelivery,
      businessLine,
      shippingCondition,
    };
  },
);

/**
 * Returns the section of application store dealing with default contracts on the specification page
 * @type {MemoizedSelector<object, {[p: string]: ISalesDocument; [p: number]: ISalesDocument}>}
 */
export const getDefaultContracts = createSelector(
  getSpecificationPage,
  state => {
    return state.specificationForm.productSelectionProducts.reduce((acc, product) => {
      return {
        ...acc,
        [_safe(() => product.productId || product.product.productId)]: product.salesDocument,
      };
    }, {});
  },
);

/**
 * Returns the section of application store dealing with available contracts on the specification page
 * @type {MemoizedSelector<object, {[p: string]: ISalesDocument[]}>}
 */
export const getAvailableContracts = createSelector(
  getSpecificationPage,
  state => state.contracts.availableContracts,
);

/**
 * Returns the section of application store dealing with available contracts with balance on the specification page
 * @type {MemoizedSelector<object, {[p: string]: ISalesDocument[]}>}
 */
export const getAvailableContractsBalance = createSelector(
  getSpecificationPage,
  state => state.contracts.contractsBalance,
);

export const getContractPayments = createSelector(
  getSpecificationPage,
  state => state.contracts.contractsPayments,
);

/**
 * Merges the lists of available and default contracts, and return them as a single list.
 *
 * @type {MemoizedSelector<object, {availableContracts: {[p: string]: ISalesDocument[]}; defaultContracts: {[p: string]: ISalesDocument}}>}
 */
export const getContractSelectData = createSelector(
  getAvailableContracts,
  getDefaultContracts,
  getMasterContractState,
  getAvailableContractsBalance,
  (
    availableContracts,
    defaultContracts,
    masterContract,
    contractsBalance,
  ): {
    availableContracts: { [productId: string]: ISalesDocument[] };
    defaultContracts: { [productId: string]: ISalesDocument };
    masterContract: {
      selectedContract: ISalesDocument;
      selectedDate: { date: Date; time: Moment };
    };
    contractsBalance: { [salesDocumentItemId: string]: ContractBalance };
  } => {
    return {
      availableContracts,
      defaultContracts,
      masterContract,
      contractsBalance,
    };
  },
);

export const getContractsAll = createSelector(
  getAvailableContracts,
  availableContracts => {
    return Object.keys(availableContracts).reduce((acc: ISalesDocument[], productId) => {
      acc.push(...availableContracts[productId]);
      return acc;
    }, []);
  },
);
